import moment from "moment";
import React, { useState, useEffect, useRef } from "react";
import DayPicker from "react-day-picker";
import MomentLocaleUtils from "react-day-picker/moment";
import {
  DATE_ONLY_FORMAT,
  PULSE_DATE_ONLY_FORMAT
} from "../../utils/constants";
import { isDayShouldBeDisabled } from "../../utils/date-picker-helper";
import { dayPickerClassNames } from "../../utils/day-picker-class-names-builder";
import themePropTypes from "../theme-prop-types";

import "moment/min/locales";

const ReactDatePicker = ({
  inline,
  value,
  minDate,
  maxDate,
  onChange,
  mobile,
  invalid,
  placeholderText,
  components,
  includeDates,
  // eslint-disable-next-line no-unused-vars
  isClearable, // Do not know what this prop was using for
  label,
  usePulseStyles = false,
  showCalendarIcon = false,
  appData,
  inputAriaLabel,
  name = ""
}) => {
  const wrapperClassName = `date-picker-wrapper-web-sdk${
    mobile ? " mobile" : ""
  }${invalid ? " invalid" : ""}`;

  // isDayShouldBeDisabled - function that get 'includeDates', 'minDate' and 'maxDate' as params,
  // use this params to create some logic conditionals and returns new function that get 'day' as param
  // and according to created conditionals check if day should be disabled or not
  // (returns true if day should be disabled and false if not)
  const disabledDays = isDayShouldBeDisabled({
    includeDates,
    minDate,
    maxDate
  });

  // onDayClick - function that is called when user select certain day,
  // and if selected day is not disabled calls 'onChange'
  const onDayClick = (day, { "react-datepicker__day--disabled": disabled }) => {
    // "react-datepicker__day--disabled" is how we named disabled days in day-picker-class-names-builder.js
    // it may be good idea to make all className values as a constants and use constant instead of string...
    if (!disabled) {
      return onChange(day);
    }
  };

  const enabledDates =
    includeDates && includeDates.length
      ? includeDates.filter(day => !disabledDays(day))
      : [];
  const fromMonth =
    enabledDates && enabledDates.length
      ? new Date(Math.min(...enabledDates))
      : minDate instanceof Date && !Number.isNaN(minDate) // check if minDate returns "Invalid Date"
      ? minDate
      : undefined;
  const toMonth =
    enabledDates && enabledDates.length
      ? new Date(Math.max(...enabledDates))
      : maxDate;

  const dayPickerProps = {
    classNames: dayPickerClassNames,
    showOutsideDays: true,
    enableOutsideDaysClick: false,
    initialMonth: fromMonth,
    disabledDays,
    fromMonth,
    toMonth,
    selectedDays: value,
    onDayClick,
    month: value,
    localeUtils: MomentLocaleUtils,
    locale: appData.locale
  };

  const { required_field } = appData?.settings?.translations || {};
  const requiredAriaLabel = inputAriaLabel || name.replace("*", required_field);

  return inline ? (
    <InlineDatePicker
      dayPickerProps={dayPickerProps}
      wrapperClassName={wrapperClassName}
    />
  ) : (
    <PopperDatePicker
      dayPickerProps={dayPickerProps}
      wrapperClassName={wrapperClassName}
      value={value}
      translations={appData?.settings?.translations}
      placeholderText={placeholderText}
      Input={components?.Input}
      label={label}
      usePulseStyles={usePulseStyles}
      showCalendarIcon={showCalendarIcon}
      inputAriaLabel={requiredAriaLabel}
      name={name}
    />
  );
};

// InlineDatePicker - calendar in default page flow
const InlineDatePicker = ({ dayPickerProps, wrapperClassName }) => {
  return (
    <div className={wrapperClassName}>
      <DayPicker {...dayPickerProps} />
    </div>
  );
};

// PopperDatePicker - input field, after clicking on which the calendar will drops
const PopperDatePicker = ({
  dayPickerProps,
  wrapperClassName,
  placeholderText,
  Input,
  translations,
  usePulseStyles,
  value,
  label,
  showCalendarIcon,
  inputAriaLabel,
  name
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef();
  const inputRef = useRef();

  const { date_picker_expanded, date_picker_collapsed } = translations;
  const dropdownAriaLabel = isOpen
    ? date_picker_expanded
    : date_picker_collapsed;
  const dateFormat = usePulseStyles ? PULSE_DATE_ONLY_FORMAT : DATE_ONLY_FORMAT;

  function closeIfDoNotContains(event) {
    // close DatePicker if target not inside wrapper
    if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  }

  useEffect(() => {
    // creates a listeners to close DatePicker if it lost focus or if user clicked outside
    document.addEventListener("focusin", closeIfDoNotContains);
    document.addEventListener("mousedown", closeIfDoNotContains);

    return () => {
      // removed listeners when component is destroyed
      document.removeEventListener("mousedown", closeIfDoNotContains);
      document.removeEventListener("focusin", closeIfDoNotContains);
    };
  }, [wrapperRef]);

  useEffect(() => {
    if (isOpen) {
      // focus date input before closing calendar fo WCAG purpose
      inputRef.current.focus();
      // close DatePicker when value is selected
      setIsOpen(false);
    }
  }, [value]);

  return (
    <div className={wrapperClassName} ref={wrapperRef}>
      <div className={`react-datepicker-wrapper ${isOpen ? "expanded" : ""}`}>
        <div className='react-datepicker__input-container'>
          <Input
            value={value ? moment(value).format(dateFormat) : ""}
            label={label}
            placeholder={placeholderText}
            name={name || "datepicker"}
            ariaLabel={inputAriaLabel}
            disableAutocomplete
            inputmode='none'
            onFocus={e => {
              e.preventDefault();
              setIsOpen(true);
            }}
            ref={inputRef}
            onChange={e => {
              e.preventDefault();
            }} // To disable input
          />
          <button
            className={`date-picker-toggle-button ${
              showCalendarIcon ? "calendar-icon" : "default-icon"
            } react-datepicker-ignore-onclickoutside`}
            aria-label={dropdownAriaLabel}
            aria-live='polite'
            onClick={e => {
              e.preventDefault();
              setIsOpen(!isOpen);
            }}
            type='button'
            tabIndex='-1' // to forbid focusing button
          />
        </div>
      </div>
      {isOpen && (
        <div className='DayPickerInput-OverlayWrapper'>
          <div className='DayPickerInput-Overlay'>
            <div className='react-datepicker-popper'>
              <DayPicker {...dayPickerProps} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

ReactDatePicker.propTypes = themePropTypes.datePicker;

InlineDatePicker.propTypes = themePropTypes.inlineDatePicker;

PopperDatePicker.propTypes = themePropTypes.popperDatePicker;

export default ReactDatePicker;
